import moment from 'moment'
import AudioPlayer from 'app/components/AudioPlayer'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import { extractionHintsValidator } from 'app/components/call-data-extraction-slug/extractionHintsValidator'
import { SmartForm } from 'app/components/form/SmartForm'
import type { UseCallDataExtractionSlugReturn } from './useCallDataExtractionSlug'

export type CallDataExtractionSlugShadcnComponentProps = UseCallDataExtractionSlugReturn

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validator = extractionHintsValidator as any

export function CallDataExtractionSlugShadcnComponent({
  transcription,
  call,
  loading,
  onSubmit,
}: CallDataExtractionSlugShadcnComponentProps) {
  return (
    <div className="flex h-full w-full">
      {loading && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
          The call is still being processed for transcription and data extraction, refresh this page in a few minutes.
        </div>
      )}
      <div className="flex h-full w-1/2 flex-col gap-3">
        <div className="text-sm text-muted-foreground">
          {call.id} — {moment(call.callDate).toLocaleString()}
        </div>
        <AudioPlayer
          id={call.id}
          callId={call.id}
          waveforms={call?.waveforms}
          audioDuration={call?.recordingDuration}
        />
        <div className="grow overflow-auto whitespace-pre-wrap text-sm">{transcription}</div>
      </div>

      <div className="w-1/2 overflow-auto p-5">
        {call.aiFields && call.aiFields.length > 0 && (
          <Table className="mb-6">
            <TableHeader>
              <TableRow>
                <TableHead>Field</TableHead>
                <TableHead>Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {call.aiFields.map((patient, i) =>
                aiFieldLabels.map(([key, label]) => (
                  <TableRow key={`${i}-${key}`}>
                    <TableCell className="font-medium">{label}</TableCell>
                    <TableCell>{patient[key as keyof typeof patient] ?? `—`}</TableCell>
                  </TableRow>
                )),
              )}
            </TableBody>
          </Table>
        )}

        <SmartForm
          replace
          validator={validator}
          onSubmit={onSubmit}
          hiddenInputs={[{ name: `callId`, value: call.id }]}
          hideButtons
        >
          <div className="rounded-lg border border-default bg-surface p-6">
            <h3 className="mb-4 text-sm font-semibold text-strong">Data Extraction Prompts</h3>
            <div className="flex flex-col gap-4">
              {extractionFields.map((field) => (
                <div key={field.name}>
                  <Label htmlFor={field.name} className="text-sm font-medium">
                    {field.label}
                  </Label>
                  <Input id={field.name} name={field.name} className="mt-1" />
                </div>
              ))}
              <div className="flex justify-between pt-4">
                <Button type="reset" variant="secondary" size="sm">
                  Reset
                </Button>
                <Button type="submit" variant="primary" size="sm">
                  Rerun data extraction
                </Button>
              </div>
            </div>
          </div>
        </SmartForm>
      </div>
    </div>
  )
}

const aiFieldLabels: [string, string][] = [
  [`appointmentDate`, `Appointment Date`],
  [`appointmentTime`, `Appointment Time`],
  [`callerPhone`, `Caller Phone`],
  [`patientName`, `Patient Name`],
  [`patientBirthdate`, `Patient Birthdate`],
  [`treatment`, `Treatment`],
  [`newPatient`, `New Patient`],
  [`insuranceProvider`, `Insurance`],
  [`patientScheduled`, `Patient Scheduled`],
  [`callOutcome`, `Call Outcome`],
  [`staff`, `Staff`],
  [`referral`, `Referral`],
]

const extractionFields = [
  { name: `patientName`, label: `Patient Name Hint` },
  { name: `patientBirthdate`, label: `Patient Birthdate Hint` },
  { name: `treatment`, label: `Treatment Hint` },
  { name: `appointmentDate`, label: `Appointment Date Hint` },
  { name: `appointmentTime`, label: `Appointment Time Hint` },
  { name: `callerPhone`, label: `Caller Phone Hint` },
  { name: `staff`, label: `Staff Hint` },
  { name: `callOutcome`, label: `Call Outcome Hint` },
  { name: `referral`, label: `Referral Hint` },
  { name: `newPatient`, label: `New Patient Hint` },
  { name: `insuranceProvider`, label: `Insurance Provider Hint` },
  { name: `patientScheduled`, label: `Patient Scheduled Hint` },
]
